import { createSlice } from '@reduxjs/toolkit';
import { type RootState } from 'store';
import { type Device } from 'types/device.types';
import { type DeviceFuelData } from 'api/control-room.api';
import { calculateTimeStampFromCurrentTime } from 'utils/helpers/dateTime';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface IDeviceState {
  unassociatedDevices: Device[];
  /**
   * Latest tag data for all associated devices
   * Fetched from dynamoDB, refreshes every 5 seconds
   */
  latestTagData: Record<string, Record<string, string | number>>;
  /**
   * Raw latest=true sensor snapshot per device (plain replace, nulls kept).
   * Unlike latestTagData, this is NOT timestamp-merged — it reflects exactly
   * the current latest=true reading so consumers can prioritise live values
   * and hide warnings when the live value is null.
   */
  liveTagData: Record<string, Record<string, string | number | null>>;
  latestOperationalData: Record<string, any> & {
    startTs?: string;
    endTs?: string;
    currentShift?: string;
    scheduledShiftDuration?: number;
  };
  isLoadingSensorData: boolean;
  isLoadingOperationalData: boolean;
}

const initialState: IDeviceState = {
  unassociatedDevices: [],
  latestTagData: {},
  liveTagData: {},
  latestOperationalData: {},
  isLoadingSensorData: true,
  isLoadingOperationalData: true,
};

export const getDaysBehind = (data: any): number => {
  if (!data.latest_ts) return -1;

  const daysInMs = calculateTimeStampFromCurrentTime(data.latest_ts);
  const days = daysInMs / (24 * 60 * 60 * 1000);

  // Return the number of days as a floating-point number
  return parseFloat(days.toFixed(2));
};

const ingestDateFormat = (data: any): string => {
  if (!data.latest_ts) return 'NA';
  const getDate = new Date(Number(data.latest_ts));
  const year = getDate.getFullYear();
  const month = String(getDate.getMonth() + 1).padStart(2, '0');
  const day = String(getDate.getDate()).padStart(2, '0');
  const hour = String(getDate.getHours()).padStart(2, '0');
  const minute = String(getDate.getMinutes()).padStart(2, '0');
  return `${month}/${day}/${year}, ${hour}:${minute}`;
};

export const findLatestTimestamp = (
  data: Record<string, any>
): number | null => {
  if (data.latest_ts) return Number(data.latest_ts);

  // Fallback: scan for any field ending in _ts
  let maxTs = 0;
  for (const key in data) {
    if (key.endsWith('_ts')) {
      const ts = Number(data[key]);
      if (!isNaN(ts) && ts > maxTs) {
        maxTs = ts;
      }
    }
  }
  return maxTs > 0 ? maxTs : null;
};

const findExistingDeviceKey = (
  latestOperationalData: Record<string, any>,
  deviceId: string
): string => {
  if (latestOperationalData[deviceId]) return deviceId;
  if (latestOperationalData[deviceId.toLowerCase()])
    return deviceId.toLowerCase();
  if (latestOperationalData[deviceId.toUpperCase()])
    return deviceId.toUpperCase();
  return deviceId;
};

const normalizeFuelData = (
  fuelData: Record<string, any> | null | undefined
): Record<string, any> => {
  if (!fuelData) return {};
  return {
    fuelLeftPercent: fuelData.percentage_fuel_left ?? null,
    fuelAlertLevel: fuelData.alert_level ?? null,
  };
};

const deepMergeTagData = (
  existingData: Record<string, Record<string, string | number>>,
  newData: Record<string, Record<string, string | number>>
) => {
  const result = { ...existingData };

  for (const deviceId in newData) {
    if (Object.prototype.hasOwnProperty.call(newData, deviceId)) {
      // If device doesn't exist in existing data, add it
      if (!result[deviceId]) {
        result[deviceId] = { ...newData[deviceId] };
        continue;
      }

      // Deep merge the device data with timestamp awareness
      result[deviceId] = {
        ...result[deviceId],
        ...Object.entries(newData[deviceId]).reduce<
          Record<string, string | number>
        >((acc, [key, value]) => {
          // Check for meta fields FIRST (always update these)
          const isMeta =
            key === 'daysBehind' ||
            key === 'ingestDate' ||
            key === 'latest_datetime';

          if (isMeta) {
            // Meta fields are always updated if present
            if (value !== null && value !== undefined) {
              acc[key] = value;
            }
            return acc;
          }

          // Only update sensor data if value is different
          if (result[deviceId][key] !== value) {
            // For timestamp fields: only update if new timestamp is fresher
            if (key.endsWith('_ts')) {
              const existingTs = result[deviceId][key];
              const newTs = Number(value);
              // Only update if existing timestamp is stale (older)
              if (
                existingTs === undefined ||
                existingTs === null ||
                Number(existingTs) < newTs
              ) {
                acc[key] = value;
                // Also update the corresponding value field
                const fieldName = key.replace('_ts', '');
                if (newData[deviceId][fieldName] !== undefined) {
                  acc[fieldName] = newData[deviceId][fieldName];
                }
              }
            } else if (!key.endsWith('_ts')) {
              // For sensor value fields: only update if fresher timestamp
              // AND never overwrite with null/undefined
              if (value !== null && value !== undefined) {
                const tsKey = `${key}_ts`;
                const newTs = Number(newData[deviceId][tsKey]);
                const existingTs = result[deviceId][tsKey];

                if (
                  !isNaN(newTs) &&
                  (existingTs === undefined ||
                    existingTs === null ||
                    Number(existingTs) < newTs)
                ) {
                  acc[key] = value;
                }
              }
            }
          }
          return acc;
        }, {}),
      };
    }
  }

  return result;
};

const mergeWithOperationalDataByTimestamp = (
  latestTagData: Record<string, Record<string, string | number>>,
  operationalData: Record<string, any>
): Record<string, Record<string, string | number>> => {
  const result = { ...latestTagData };

  // For each device in operational data
  for (const deviceId in operationalData) {
    if (Object.prototype.hasOwnProperty.call(operationalData, deviceId)) {
      const opData = operationalData[deviceId];

      // Initialize device in result if not present
      if (!result[deviceId]) {
        result[deviceId] = {};
      }

      // Merge each field from operational data
      for (const key in opData) {
        if (Object.prototype.hasOwnProperty.call(opData, key)) {
          // Check for meta fields FIRST (always update these)
          const isMeta =
            key === 'daysBehind' ||
            key === 'ingestDate' ||
            key === 'latest_datetime';

          if (isMeta) {
            // Meta fields are always updated if present
            if (opData[key] !== null && opData[key] !== undefined) {
              result[deviceId][key] = opData[key];
            }
            continue;
          }

          // If this is a timestamp field (ends with _ts), compare with existing
          if (key.endsWith('_ts')) {
            const existingTs = result[deviceId][key];
            const newTs = Number(opData[key]);

            // If existing timestamp is stale (older), prefer operational data
            if (existingTs === undefined || Number(existingTs) < newTs) {
              // Update this timestamp AND its corresponding value field
              const fieldName = key.replace('_ts', '');
              result[deviceId][key] = opData[key];

              // Also update the value field if operational data has it
              if (opData[fieldName] !== undefined) {
                result[deviceId][fieldName] = opData[fieldName];
              }
            }
          } else if (!key.endsWith('_ts')) {
            // For sensor value fields: only update if fresher timestamp
            if (opData[key] !== null && opData[key] !== undefined) {
              const tsKey = `${key}_ts`;
              const opDataTs = Number(opData[tsKey]);
              const existingTs = result[deviceId][tsKey];

              // Update if: no existing value, or operational data is fresher
              if (
                result[deviceId][key] === undefined ||
                existingTs === undefined ||
                (opDataTs > Number(existingTs) && !isNaN(opDataTs))
              ) {
                result[deviceId][key] = opData[key];
              }
            }
          }
        }
      }
    }
  }

  return result;
};

export const deviceSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    getAll(state) {
      return {
        ...state,
      };
    },
    setUnassociatedDeviceList(state, { payload }) {
      return {
        ...state,
        unassociatedDevices: payload,
      };
    },
    updateUnassociatedDeviceList(state, { payload }) {
      let isDeviceInList = false;
      // either remove or add the device to the list
      let updatedUnassociatedDevices = state.unassociatedDevices.map(
        (device) => {
          if (device.deviceId === payload.deviceId) {
            isDeviceInList = true;

            return payload;
          }
          return device;
        }
      );

      // device is unassociated but not found, it should be in the list
      if (!isDeviceInList && !payload.assetId) {
        updatedUnassociatedDevices.push(payload);
      }

      // device is associated and should be removed from the list
      if (isDeviceInList && payload.assetId) {
        updatedUnassociatedDevices = updatedUnassociatedDevices.filter(
          (device) => device.deviceId !== payload.deviceId
        );
      }

      return {
        ...state,
        unassociatedDevices: updatedUnassociatedDevices,
      };
    },
    setDevicesWithTagList(state, { payload }) {
      if (payload) {
        for (const deviceId in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, deviceId)) {
            const deviceData = payload[deviceId];
            deviceData.daysBehind = getDaysBehind(deviceData);
            deviceData.ingestDate = ingestDateFormat(deviceData);
          }
        }
      }

      return {
        ...state,
        latestTagData: deepMergeTagData(state.latestTagData, payload),
      };
    },
    setLatestOperationalData(state, { payload }) {
      // Smart merge: prefer fresher timestamps from operational data
      const mergedTagData = mergeWithOperationalDataByTimestamp(
        state.latestTagData,
        payload
      );

      // The operational-data poll payload doesn't carry fuel fields, so a
      // bare replace would wipe out what setInitialFuelData/patchLiveDeviceState
      // already set. Carry them forward onto the new payload per device.
      const previousData = state.latestOperationalData;
      for (const deviceId in previousData) {
        if (!Object.prototype.hasOwnProperty.call(previousData, deviceId)) {
          continue;
        }
        const prevDevice = previousData[deviceId];
        if (
          !prevDevice ||
          typeof prevDevice !== 'object' ||
          (prevDevice.fuelLeftPercent === undefined &&
            prevDevice.fuelAlertLevel === undefined)
        ) {
          continue;
        }
        const key = findExistingDeviceKey(payload, deviceId);
        if (!payload[key]) continue;
        payload[key].fuelLeftPercent = prevDevice.fuelLeftPercent;
        payload[key].fuelAlertLevel = prevDevice.fuelAlertLevel;
      }

      state.latestOperationalData = payload;
      state.latestTagData = mergedTagData;
    },
    setLiveTagData(state, { payload }) {
      // Plain per-device replace (no timestamp merge) so the raw latest=true
      // snapshot — including null tire values — is preserved as-is.
      if (!payload) return;
      for (const deviceId in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, deviceId)) {
          state.liveTagData[deviceId] = payload[deviceId];
        }
      }
    },
    setIsLoadingSensorData(state, { payload }) {
      state.isLoadingSensorData = payload;
    },
    setIsLoadingOperationalData(state, { payload }) {
      state.isLoadingOperationalData = payload;
    },
    resetDevices(state) {
      state = initialState;
    },
    patchLiveDeviceState(
      state,
      {
        payload,
      }: { payload: { deviceId: string; liveState: Record<string, any> } }
    ) {
      const { deviceId, liveState } = payload;
      if (!deviceId) return;
      const { fuelData, ...restLiveState } = liveState;
      const key = findExistingDeviceKey(state.latestOperationalData, deviceId);
      state.latestOperationalData[key] = {
        ...(state.latestOperationalData[key] ?? {}),
        ...restLiveState,
        ...normalizeFuelData(fuelData),
      };
    },
    setInitialFuelData(state, { payload }: { payload: DeviceFuelData[] }) {
      if (!payload) return;
      for (const device of payload) {
        if (!device.deviceId || !device.fuelData) continue;
        const key = findExistingDeviceKey(
          state.latestOperationalData,
          device.deviceId
        );
        state.latestOperationalData[key] = {
          ...(state.latestOperationalData[key] ?? {}),
          ...normalizeFuelData(device.fuelData),
        };
      }
    },
  },
});

export const {
  getAll,
  setUnassociatedDeviceList,
  updateUnassociatedDeviceList,
  setDevicesWithTagList,
  setLatestOperationalData,
  setLiveTagData,
  setIsLoadingSensorData,
  setIsLoadingOperationalData,
  resetDevices,
  patchLiveDeviceState,
  setInitialFuelData,
} = deviceSlice.actions;

export const selectLatestTagData = (state: RootState) =>
  state.deviceReducer.latestTagData;

export const selectLiveTagData = (state: RootState) =>
  state.deviceReducer.liveTagData;

export default deviceSlice.reducer;
